/**
 * WebGL による 3D めくりエンジン（FR-FLIP-01）。
 *
 * 表示の論理（綴じ・見開き・テクスチャの保持範囲）は css2d と同じ共通層を使い、
 * 違いは「どう描くか」だけに閉じる。現在ページの真実は core が持ち、ここは描画に徹する。
 * カスタムシェーダは使わない（設計書の方針）。
 */
import { cls } from '../../brand';
import { DisposeRegistry } from '../../core/dispose-registry';
import type { ResourceCounts } from '../../core/dispose-registry';
import type { EngineContext, EngineContextPatch, RenderEngine, Viewport } from '../../core/engine';
import type { PageRaster } from '../../core/source';
import { MAX_CANVAS_AREA, MAX_CANVAS_SIDE } from '../../source/canvas-limits';
import type { Texture } from 'three';
import { REGION_MARGIN, REGION_STEP } from '../constants';
import {
  fitPageBox,
  flipPlan,
  pageScreenRect,
  pageSize,
  pageSlot,
  pagesOf,
  resolveMode,
  spreadAt,
  spreadOf,
  singleFlipPlan,
  spreadShift,
} from '../layout';
import type { PageSlot, ScreenRect, Spread } from '../layout';
import {
  inflateRegion,
  isFullPage,
  pageRegion,
  planRaster,
  quantizeRegion,
  visibleBox,
} from '../region';
import { TextureStore } from '../texture-store';
import { centerOf, panForCenter, toPageLocal, type PageSlotRect } from '../viewport';
import { easeQuad, flipAngle } from './bend';
import type { SheetSide } from './bend';
import {
  DETAIL_DELAY_MS,
  DETAIL_MIN_ZOOM,
  DETAIL_PLANE_Z,
  FLIP_MS,
  FLIP_RENDER_SCALE,
  MAX_TEXTURE,
  WINDOW_PAGES,
} from './constants';
import { createScene, fitScene, setRenderScale } from './scene';
import type { PageMesh, SceneBundle } from './scene';
import { Sheet } from './sheet';
import { TextureCache } from './textures';
import { clampPan, pixelRatio } from './units';

export class Webgl3dEngine implements RenderEngine {
  private readonly registry = new DisposeRegistry();

  private context: EngineContext | null = null;
  private view: Window | null = null;
  private stage: HTMLDivElement | null = null;
  private bundle: SceneBundle | null = null;
  private store: TextureStore | null = null;
  private cache: TextureCache | null = null;

  private current: Spread = { index: 0, left: null, right: null };
  private currentPage = 1;
  private mode: 'single' | 'spread' = 'single';
  private box = { width: 0, height: 0 };
  private zoom = 1;
  private pan = { x: 0, y: 0 };
  /** 対向ページが無いときに 1 枚を中央へ寄せる量。 */
  private shift = 0;

  private sheet: Sheet | null = null;
  private flip: {
    start: number;
    rigid: boolean;
    /** 逆再生（戻るめくり — 倒れていた紙が起き上がる）。 */
    reverse: boolean;
    fromShift: number;
    toShift: number;
    resolve: () => void;
  } | null = null;
  private raf = 0;
  private needsRender = false;
  /** フレーム処理中か（この間の描画要求は tick の末尾がまとめて拾う）。 */
  private ticking = false;
  private cancelReraster: (() => void) | null = null;
  private container = { width: 0, height: 0 };
  /** 現在の描画解像度（静止時 1・めくり中は落とす）。 */
  private renderScale = 1;

  get spread(): boolean {
    return this.mode === 'spread';
  }

  pageRect(pageNo: number): ScreenRect | null {
    const host = this.stage?.parentElement;
    if (!host) return null;
    const slot: PageSlot | null = pageSlot(pageNo, this.mode, this.current, this.currentPage);
    if (!slot) return null;
    const container = { width: host.clientWidth, height: host.clientHeight };
    return pageScreenRect(container, this.box, slot, this.shift, this.zoom, this.pan);
  }

  mount(host: HTMLElement, context: EngineContext): void {
    this.context = context;
    const doc = host.ownerDocument;
    const view = doc.defaultView;
    if (!view) throw new Error('ウィンドウを取得できない');
    this.view = view;

    const stage = doc.createElement('div');
    stage.className = cls('stage');
    const canvas = doc.createElement('canvas');
    canvas.className = cls('canvas');
    // 誌面の中身は canvas では支援技術に届かない。代替テキストは ui/page-text.ts が
    // 別に出すので、canvas 自体は装飾として伏せる（WCAG 1.1.1・M8c-2 手順 7）。
    canvas.setAttribute('aria-hidden', 'true');
    stage.appendChild(canvas);
    host.appendChild(stage);
    this.stage = stage;

    try {
      this.bundle = createScene(canvas, view);
    } catch (error) {
      stage.remove();
      this.stage = null;
      throw error;
    }

    this.store = new TextureStore((request) => context.raster(request));
    this.cache = new TextureCache(this.bundle.renderer, this.store);

    // GL コンテキストを失ったら core へ知らせる（core が 2D へ降格し fallback を発火する）。
    this.registry.listen(canvas, 'webglcontextlost', () => {
      context.bus.emit('engine:lost', { reason: 'context-lost' });
    });
    this.registry.add('other', () => {
      if (this.raf) view.cancelAnimationFrame(this.raf);
      this.raf = 0;
    });

    // 描画先の寸法変化を自分で追う。canvas の描画バッファは CSS では追従しないため、
    // ツールバーの挿入などでホストが縮んだら描き直す必要がある。
    const observer = this.registry.observer(new ResizeObserver(() => this.resize()));
    observer.observe(host);

    this.layout();
  }

  async goTo(pageNo: number, options: { animate: boolean }): Promise<void> {
    const context = this.requireContext();
    const target = spreadAt(spreadOf(pageNo), context.pages.length, context.direction);
    const forward =
      target.index === this.current.index
        ? pageNo > this.currentPage
        : target.index > this.current.index;

    const from = this.current;
    const fromPage = this.currentPage;
    // 単ページ表示（スマートフォンなど）でもめくる — 見開きのときだけ動く実装だと、
    // スマートフォンでは常にアニメーションが出ない。
    const changed = this.mode === 'spread' ? target.index !== from.index : pageNo !== fromPage;
    const useFlip = options.animate && !context.reducedMotion && changed;

    // ページが変われば詳細面は無効（めくらない遷移でも同じ）
    this.cancelDetail();
    this.current = target;
    this.currentPage = pageNo;
    context.bus.emit('engine:flipStart', { direction: forward ? 'forward' : 'back' });

    if (useFlip) await this.animateFlip(from, target, forward, fromPage);
    else await this.paintCurrent();

    context.bus.emit('engine:flipEnd', { page: pageNo });
  }

  setZoom(level: number, focal?: { x: number; y: number }): void {
    this.zoom = level;
    if (level <= 1) {
      this.pan = { x: 0, y: 0 };
    } else if (focal) {
      this.pan = this.clamp({
        x: (0.5 - focal.x) * (level - 1) * this.bookWidth(),
        y: (0.5 - focal.y) * (level - 1) * this.box.height,
      });
    } else {
      this.pan = this.clamp(this.pan);
    }
    this.applyTransform();
    this.invalidate();
    this.scheduleDetail();
  }

  getViewport(): Viewport | null {
    if (this.box.width === 0 || this.container.width === 0) return null;
    const center = centerOf(this.container, this.bookBox(), this.zoom, this.pan, this.shift);
    const hit = toPageLocal(center, this.slotRects());
    return hit ? { ...hit, zoom: this.zoom } : null;
  }

  setViewport(viewport: Viewport): void {
    const slot = this.slotRects().find((entry) => entry.page === viewport.page);
    if (!slot || this.box.width === 0) return;
    this.zoom = viewport.zoom;
    this.pan =
      viewport.zoom <= 1
        ? { x: 0, y: 0 }
        : this.clamp(
            panForCenter(this.bookBox(), slot.rect, viewport.x, viewport.y, viewport.zoom, this.shift),
          );
    this.applyTransform();
    this.invalidate();
    this.scheduleDetail();
  }

  panBy(dx: number, dy: number): void {
    if (this.zoom <= 1) return;
    this.pan = this.clamp({ x: this.pan.x + dx, y: this.pan.y + dy });
    this.applyTransform();
    this.invalidate();
    // 拡大中はパンで可視領域が変わる = 詳細面を焼き直す必要がある
    this.scheduleDetail();
  }

  resize(): void {
    this.cancelDetail();
    this.layout();
    void this.paintCurrent();
  }

  setContext(patch: EngineContextPatch): void {
    const context = this.requireContext();
    Object.assign(context, patch);
    this.cancelDetail();
    this.current = spreadAt(spreadOf(this.currentPage), context.pages.length, context.direction);
    this.layout();
    void this.paintCurrent();
  }

  unmount(): void {
    this.cancelFlip();
    this.registry.disposeAll();
    this.stage?.remove();
    this.stage = null;
  }

  dispose(): void {
    this.unmount();
    this.sheet?.dispose();
    this.sheet = null;
    this.cache?.dispose();
    this.cache = null;
    this.store?.dispose();
    this.store = null;
    this.bundle?.dispose();
    this.bundle = null;
    this.context = null;
    this.view = null;
  }

  resourceCounts(): ResourceCounts {
    return this.registry.activeCounts();
  }

  // -- 内部 ----------------------------------------------------------------

  private requireContext(): EngineContext {
    if (!this.context) throw new Error('エンジンが未マウント');
    return this.context;
  }

  /** コンテナ寸法からモードとページ箱を決め、シーンへ反映する。 */
  private layout(): void {
    const context = this.requireContext();
    const host = this.stage?.parentElement;
    const bundle = this.bundle;
    const view = this.view;
    if (!host || !bundle || !view) return;

    const container = { width: host.clientWidth, height: host.clientHeight };
    this.container = container;
    this.mode = resolveMode(context.mode, container, context.pages.length);
    const halves = this.mode === 'spread' ? 2 : 1;
    this.box = fitPageBox(container, pageSize(context.pages, this.currentPage), halves);

    fitScene(bundle, container, this.box, halves, view, this.renderScale);
    this.shift = this.mode === 'spread' ? spreadShift(this.current, this.box.width) : 0;
    this.applyTransform();
    this.invalidate();
  }

  private bookWidth(): number {
    return this.box.width * (this.mode === 'spread' ? 2 : 1);
  }

  private clamp(pan: { x: number; y: number }): { x: number; y: number } {
    return clampPan(pan, this.bookWidth(), this.box.height, this.zoom);
  }

  private bookBox(): { width: number; height: number } {
    return { width: this.bookWidth(), height: this.box.height };
  }

  /** 表示中のページが本ローカル座標で占める矩形。 */
  private slotRects(): PageSlotRect[] {
    const visible = this.visiblePages();
    const rects: PageSlotRect[] = [];
    const push = (page: number | null, offsetX: number): void => {
      if (page === null) return;
      rects.push({ page, rect: { x: offsetX, y: 0, w: this.box.width, h: this.box.height } });
    };
    push(visible.left, 0);
    push(visible.right, this.mode === 'spread' ? this.box.width : 0);
    return rects;
  }

  /**
   * ズーム・パン・中央寄せをシーンへ書き込む。
   * 描画の要求（invalidate）は呼び出し側の責任 — めくり中は既にフレームを回しているため、
   * ここで invalidate するとフレームが二重に積まれ、描画が 2 倍になる。
   */
  private applyTransform(shift: number = this.shift): void {
    const book = this.bundle?.book;
    if (!book) return;
    book.scale.set(this.zoom, this.zoom, 1);
    // CSS の y は下向き、3D の y は上向き。
    book.position.set(this.pan.x + shift, -this.pan.y, 0);
  }

  /** テクスチャ 1 枚の上限（端末・ソース・自前の上限のうち最小）。 */
  private rasterCap(pageNo: number): number {
    const context = this.requireContext();
    const deviceMax = this.bundle?.renderer.capabilities.maxTextureSize ?? MAX_TEXTURE;
    return Math.max(1, Math.min(MAX_TEXTURE, deviceMax, context.maxRasterWidth(pageNo)));
  }

  private pixelRatio(): number {
    return this.view ? pixelRatio(this.view) : 1;
  }

  /**
   * 下地テクスチャの幅。ズームを掛けない — 拡大は詳細面（領域ラスタ）が担うので、
   * 下地はズームのたびに焼き直さない。
   */
  private rasterWidth(pageNo: number): number {
    const wanted = this.box.width * this.pixelRatio();
    return Math.max(1, Math.round(Math.min(wanted, this.rasterCap(pageNo))));
  }

  /** ページ全面換算での希望解像度（拡大時に詳細面が目指す値）。 */
  private effectiveWidth(): number {
    return this.box.width * this.pixelRatio() * this.zoom;
  }

  /** 実際に左右へ出すページ（単ページ表示では現在ページのみ）。 */
  private visiblePages(): { left: number | null; right: number | null } {
    if (this.mode === 'spread') return { left: this.current.left, right: this.current.right };
    return { left: this.currentPage, right: null };
  }

  /** 保持すべきページ（表示中＋近傍・NFR-PERF-01）。 */
  private windowPages(): number[] {
    const context = this.requireContext();
    const pages = new Set(pagesOf(this.current));
    pages.add(this.currentPage);
    for (let offset = 1; offset <= WINDOW_PAGES; offset += 1) {
      for (const no of [this.currentPage - offset, this.currentPage + offset]) {
        if (no >= 1 && no <= context.pages.length) pages.add(no);
      }
    }
    return [...pages];
  }

  /** ページのテクスチャを取り、面へ貼る。 */
  private async paintPage(page: PageMesh, pageNo: number | null): Promise<void> {
    if (pageNo === null) {
      page.mesh.visible = false;
      page.material.map = null;
      page.material.needsUpdate = true;
      this.invalidate();
      return;
    }
    const texture = await this.texture(pageNo);
    if (!texture) return;
    page.material.map = texture;
    page.material.needsUpdate = true;
    page.mesh.visible = true;
    this.invalidate();
  }

  private async texture(pageNo: number): Promise<Texture | null> {
    const cache = this.cache;
    if (!cache) return null;
    try {
      return await cache.get(pageNo, this.rasterWidth(pageNo));
    } catch {
      // 中断（圏外へ出た・dispose）は静かに畳む。
      return null;
    }
  }

  /** 現在の見開きを描画し、テクスチャの保持範囲を更新する。 */
  private async paintCurrent(): Promise<void> {
    const bundle = this.bundle;
    if (!bundle) return;

    const pages = this.windowPages();
    this.store?.setWindow(pages);
    this.cache?.retain(pages);

    const visible = this.visiblePages();
    // 単ページ表示では中央 1 面だけを使う。
    const single = this.mode === 'single';
    bundle.left.mesh.position.setX(single ? 0 : -this.box.width / 2);

    await Promise.all([
      this.paintPage(bundle.left, visible.left),
      this.paintPage(bundle.right, visible.right),
    ]);
    this.scheduleDetail();
  }

  /** シートを 1 枚めくる。内訳（動く側・表裏・下に見えるページ）は共通層が決める。 */
  private async animateFlip(
    from: Spread,
    target: Spread,
    forward: boolean,
    fromPage: number,
  ): Promise<void> {
    const context = this.requireContext();
    const bundle = this.bundle;
    const view = this.view;
    if (!bundle || !view) return;

    const single = this.mode === 'single';
    const plan = single
      ? singleFlipPlan(fromPage, this.currentPage, forward, context.direction)
      : flipPlan(from, target, forward, context.direction);
    // 中央寄せは「めくる前」の位置から始め、着地後の位置へ動かす（表紙が開く動き）。
    const fromShift = single ? 0 : spreadShift(from, this.box.width);
    this.layout();

    const pages = this.windowPages();
    this.store?.setWindow(pages);
    this.cache?.retain([...pages, ...[plan.front, plan.back].filter((n): n is number => n !== null)]);

    // シートの下: めくられる側には着地後のページが現れ、反対側は着地までは現状のまま。
    await Promise.all([
      this.paintPage(bundle.left, plan.under.left),
      this.paintPage(bundle.right, plan.under.right),
    ]);

    const side: SheetSide = plan.fromRight ? 1 : -1;
    const rigid = this.isCover(plan.front) || this.isCover(plan.back);

    // シートは 1 度だけ作って使い回す（毎回作り直すとシェーダが再コンパイルされ、
    // めくり始めに数百 ms の停止が出る）。
    const sheet = (this.sheet ??= this.addSheet(bundle));
    // 見開きの綴じは本の中央（0）。単ページ表示では綴じがページ自身の端にある。
    const hingeX = single ? (-side * this.box.width) / 2 : 0;
    sheet.configure(this.box.width, this.box.height, side, rigid && context.hardcover, hingeX);

    const [front, back] = await Promise.all([
      plan.front === null ? null : this.texture(plan.front),
      plan.back === null ? null : this.texture(plan.back),
    ]);
    sheet.setTextures(front, back);

    await new Promise<void>((resolve) => {
      this.flip = {
        start: view.performance.now(),
        rigid: rigid && context.hardcover,
        reverse: plan.reverse,
        fromShift,
        toShift: this.shift,
        resolve,
      };
      this.invalidate();
    });

    sheet.hide();
    await this.paintCurrent();
  }

  /** シートを 1 枚だけ作り、本に取り付ける。 */
  private addSheet(bundle: SceneBundle): Sheet {
    const sheet = new Sheet();
    bundle.book.add(sheet.group);
    return sheet;
  }

  private isCover(pageNo: number | null): boolean {
    const context = this.requireContext();
    return pageNo === 1 || pageNo === context.pages.length;
  }

  /** 進行中のめくりを終端へ送って解決する（連続操作・dispose 用）。 */
  private cancelFlip(): void {
    const flip = this.flip;
    this.flip = null;
    this.sheet?.hide();
    flip?.resolve();
  }

  /** 描画をオンデマンドで回す（静止時は GPU を触らない）。 */
  private invalidate(): void {
    this.needsRender = true;
    // フレーム処理中に呼ばれた場合は tick の末尾が次を積むため、ここでは積まない
    // （二重に積むと同じ絵を 2 回描くことになり、弱い GPU で目に見えて重くなる）。
    if (this.raf || this.ticking || !this.view) return;
    this.raf = this.view.requestAnimationFrame(this.tick);
  }

  private readonly tick = (now: number): void => {
    this.raf = 0;
    const bundle = this.bundle;
    const view = this.view;
    if (!bundle || !view) return;
    this.ticking = true;

    // 3D の負荷は塗りつぶし面積でほぼ決まる。動いている間は解像度を落とし、
    // 止まった瞬間に元の解像度で描き直す（弱い GPU でもめくりが引っかからない）。
    const animating = this.advanceFlip(now);
    this.setRenderScale(animating ? FLIP_RENDER_SCALE : 1);

    if (this.needsRender || animating) {
      // 絵が変わるフレームだけ影マップも作り直す（静止中は再計算しない）。
      bundle.renderer.shadowMap.needsUpdate = true;
      bundle.renderer.render(bundle.scene, bundle.camera);
      this.needsRender = false;
    }
    this.ticking = false;
    if (animating || this.needsRender) this.raf = view.requestAnimationFrame(this.tick);
  };

  /** 描画解像度を切り替える（値が変わったときだけ実際に反映する）。 */
  private setRenderScale(scale: number): void {
    if (scale === this.renderScale) return;
    const bundle = this.bundle;
    const view = this.view;
    if (!bundle || !view) return;
    this.renderScale = scale;
    setRenderScale(bundle, this.container, view, scale);
    this.needsRender = true;
  }

  /** めくりを 1 フレーム進める。継続中なら true。 */
  private advanceFlip(now: number): boolean {
    const flip = this.flip;
    const sheet = this.sheet;
    if (!flip || !sheet) return false;

    const progress = Math.min(1, (now - flip.start) / FLIP_MS);
    const angle = flipAngle(progress, flip.rigid);
    // 戻るめくりは逆再生（倒れていた紙が起き上がる）。綴じ側は動かさない。
    sheet.apply(flip.reverse ? Math.PI - angle : angle);

    // 表紙が開くときは、本の中央寄せもめくりに合わせて動かす。
    if (flip.fromShift !== flip.toShift) {
      const eased = easeQuad(progress);
      this.applyTransform(flip.fromShift + (flip.toShift - flip.fromShift) * eased);
    }

    if (progress < 1) return true;
    this.applyTransform();
    this.flip = null;
    flip.resolve();
    return false;
  }

  /**
   * ズーム・パンが落ち着いたら、可視領域だけを高解像度で焼いて重ねる予約をする。
   * めくり中は決して焼かない（下地だけが正しい絵）。
   */
  private scheduleDetail(): void {
    this.cancelReraster?.();
    this.cancelReraster = null;
    if (this.flip || this.zoom < DETAIL_MIN_ZOOM) return;
    this.cancelReraster = this.registry.timeout(() => {
      this.cancelReraster = null;
      void this.paintDetail();
    }, DETAIL_DELAY_MS);
  }

  /** 予約を取り消し、重ねていた詳細面を外す（＝必ず正しい下地の絵に戻る）。 */
  private cancelDetail(): void {
    this.cancelReraster?.();
    this.cancelReraster = null;
    const bundle = this.bundle;
    if (bundle) {
      bundle.left.detail.visible = false;
      bundle.right.detail.visible = false;
    }
    this.cache?.clearDetails();
    this.store?.clearDetails();
    this.invalidate();
  }

  /** 可視領域だけを高解像度で焼き、下地の上へ重ねる（設計書 L361）。 */
  private async paintDetail(): Promise<void> {
    const context = this.context;
    const bundle = this.bundle;
    const cache = this.cache;
    if (!context || !bundle || !cache || this.flip || this.zoom < DETAIL_MIN_ZOOM) return;

    const visible = this.visiblePages();
    const pages = [visible.left, visible.right].filter((no): no is number => no !== null);
    this.store?.setDetailWindow(pages);
    cache.retainDetails(pages);

    const halves = this.mode === 'spread' ? 2 : 1;
    const book = { width: this.box.width * halves, height: this.box.height };
    const view = visibleBox(this.container, book, this.zoom, this.pan, this.shift);
    const single = this.mode === 'single';

    const faces: [PageMesh, number | null, number][] = [
      [bundle.left, visible.left, single ? 0 : -this.box.width / 2],
      [bundle.right, visible.right, this.box.width / 2],
    ];

    await Promise.all(
      faces.map(async ([face, pageNo, centerX]) => {
        face.detail.visible = false;
        if (pageNo === null) return;

        // 本ローカル（左上原点・CSS px）でのページ矩形
        const rect = {
          x: centerX + book.width / 2 - this.box.width / 2,
          y: 0,
          w: this.box.width,
          h: this.box.height,
        };
        const region = pageRegion(rect, view);
        if (!region || isFullPage(region)) return; // 全面が見えているなら下地で足りる

        const size = pageSize(context.pages, pageNo);
        const plan = planRaster({
          effectiveWidth: this.effectiveWidth(),
          aspect: size.height / size.width,
          region: quantizeRegion(inflateRegion(region, REGION_MARGIN), REGION_STEP),
          maxEffectiveWidth: context.maxEffectiveWidth(pageNo),
          maxRasterWidth: this.rasterCap(pageNo),
          maxArea: MAX_CANVAS_AREA,
          maxSide: MAX_CANVAS_SIDE,
        });

        let detail: { texture: Texture; raster: PageRaster };
        try {
          detail = await cache.getDetail(pageNo, plan.targetWidth, plan.region);
        } catch {
          return; // 中断（めくり・dispose）は静かに畳む
        }
        // await の間にめくり・ズーム解除が起きていたら重ねない（安全側）
        if (this.flip || this.zoom < DETAIL_MIN_ZOOM) return;

        const r = detail.raster.region;
        face.detailMaterial.map = detail.texture;
        face.detailMaterial.needsUpdate = true;
        face.detail.scale.set(r.w * this.box.width, r.h * this.box.height, 1);
        // 3D の y は上向き（正規化座標はページ上端が 0）。
        face.detail.position.set(
          centerX + (r.x + r.w / 2 - 0.5) * this.box.width,
          -(r.y + r.h / 2 - 0.5) * this.box.height,
          DETAIL_PLANE_Z,
        );
        face.detail.visible = true;
      }),
    );
    this.invalidate();
  }
}
